import { ChangeEvent, useState } from "react";

import { useAppSelector } from "../../../app/store";
import { getInvestigationNatures } from "../../configs/logic/Slice";

import { OptionNameValuePair } from "../../../lib/interfaces/types";
import FormElement from "../../../components/ui/formelement/FromElement";
import { TextareaV2 } from "../../../components/ui/textbox/v2";
import MultiSelectDropdownV2 from "../../../components/ui/selectdropdown/v2/MultiSelectDropdown";
import { RenderSelectedPills } from "../utils/common";
import { BackgroundState, validateField } from "../utils/helper";
import StepNavigation from "./StepNavigation";

type CaseBackgroundStepProps = {
    value: BackgroundState;
    onChange: (fieldName: string, value: any) => void;
    onNext: () => void;
    onPrevious: () => void;
};

const CaseBackgroundStep: React.FC<CaseBackgroundStepProps> = ({ value, onChange, onNext, onPrevious }) => {
    const investigationNatures = useAppSelector(getInvestigationNatures)
    const investigationNatureOptions: OptionNameValuePair<string>[] = investigationNatures.map(nature => ({name: nature, value: nature}))

    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const { investigation_nature: investigationNature, background } = value;
    const handleFormInputChange = onChange;

    const validateBackgroundStep = (v: BackgroundState): Record<string, string> => {
        const errors: Record<string, string> = {};
        (["investigation_nature", "background"] as const).forEach((field) => {
            const error = validateField(field, (v as any)[field]);
            if (error) errors[field] = error;
        });
        if (v.background && v.background.trim().length < 100) {
            errors["background"] = "Background must be at least 100 characters long.";
        }
        return errors;
    };

    const handleNext = () => {
        const errors = validateBackgroundStep(value);
        setFormErrors(errors);
        if (Object.keys(errors).length === 0) onNext();
    };

    const handleInvestigationNatureRemoval = (value: string) => {
        if (!investigationNature) return;
        const updatedNatures = investigationNature.filter(nature => nature.value !== value);
        handleFormInputChange("investigation_nature", updatedNatures);
    };
    
    return (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 gap-y-2">
            <div className="md:col-span-2">
                <FormElement
                    name="Nature of Suspicion"
                    className="mb-4"
                    inputComponent={
                        <div className="flex flex-col gap-2">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="col-span-1">
                                    <MultiSelectDropdownV2
                                        selectedValues={investigationNature}
                                        name="nature of suspicion"
                                        enableSearch={false}
                                        options={investigationNatureOptions || []}
                                        placeholder="Select nature of suspicion"
                                        onSelectionChange={handleFormInputChange.bind(null, "investigation_nature")}
                                        containerClassName="border border-gray-300 rounded-md py-2 px-3"
                                        labelClassName="font-medium"
                                    />
                                </div>
                            </div>
                            {investigationNature && investigationNature.length > 0 && (
                                <RenderSelectedPills
                                    data={investigationNature.map(nature => nature.value)}
                                    onRemove={(index: number) => handleInvestigationNatureRemoval(investigationNature[index].value)}
                                />
                            )}
                        </div>
                    }
                    error={formErrors["investigation_nature"]}
                />
            </div>
            <div className="md:col-span-2">
                <FormElement
                    name="Brief Background"
                    className="mb-4"
                    inputComponent={
                        <TextareaV2
                            name="background"
                            placeholder="Enter a brief background about the case"
                            value={background}
                            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleFormInputChange("background", e.target.value)}
                            className="resize-none py-2 px-3"
                            rows={9}
                        />
                    }
                    error={formErrors["background"]}
                />
            </div>
        </div>
        <StepNavigation onNext={handleNext} onPrevious={onPrevious} />
        </>
    );
}

export default CaseBackgroundStep;